import type { EpcProject } from '../../../services/epcProjectService';

export type EpcVisualTheme = 'solar' | 'wind' | 'hybrid' | 'bess' | 'generic';

/** Sovereign Emerald palette tokens for EPC project surfaces (matches the cinematic login). */
export const SOVEREIGN = {
  paper: '#F2EADB',
  canvas: '#FCFBF6',
  cardBg: 'rgba(255,254,251,0.96)',
  cardBorder: 'rgba(11,59,46,0.18)',
  innerRule: 'rgba(11,59,46,0.14)',
  cardShadow: '0 1px 2px rgba(11,59,46,0.04), 0 16px 40px -24px rgba(11,59,46,0.20)',
  ink: '#2A1F17',
  title: '#0B3B2E',
  brand: '#339274',
  bottle: '#1F5A47',
  champagne: '#C9A961',
  inputBg: '#fcfaf3',
  inputBorder: 'rgba(11,59,46,0.28)',
  inputHoverBorder: 'rgba(51,146,116,0.6)',
  btn: '#264f3c',
  btnHover: '#2d5c47',
  btnBorder: 'rgba(11,59,46,0.45)',
  btnShadow: '0 8px 18px -12px rgba(38,79,60,.5), inset 0 1px 0 rgba(255,255,255,.12)',
  barTrack: '#E7DCC4',
  barTrackBorder: 'rgba(11,59,46,0.10)',
  barFill: 'linear-gradient(90deg,#1F5A47,#339274)',
  barPlanned: 'rgba(31,90,71,0.30)',
  kpiCaption: '#7C8A82',
} as const;

function refName(ref: unknown): string {
  if (ref && typeof ref === 'object' && 'name' in ref) {
    return String((ref as { name?: string }).name ?? '');
  }
  return '';
}

function categoryFromPopulated(ref: unknown): EpcVisualTheme | null {
  if (!ref || typeof ref !== 'object') return null;
  const c = (ref as { category?: string }).category;
  if (c === 'solar' || c === 'wind' || c === 'hybrid' || c === 'bess') return c;
  return null;
}

/**
 * Theme for EPC UI (hero, list cards) from populated ProjectType.category or name heuristics.
 */
export function getEpcVisualTheme(project: EpcProject): EpcVisualTheme {
  const fromCat = categoryFromPopulated(project.projectTypeId);
  if (fromCat) return fromCat;
  const name = refName(project.projectTypeId).toLowerCase();
  if (name.includes('solar') || name.includes('pv') || name.includes('photovoltaic')) return 'solar';
  if (name.includes('wind')) return 'wind';
  if (name.includes('hybrid')) return 'hybrid';
  if (name.includes('bess') || name.includes('storage') || name.includes('battery')) return 'bess';
  return 'generic';
}

export const EPC_THEME_STYLES: Record<
  EpcVisualTheme,
  { gradient: string; accent: string; accentSoft: string; label: string }
> = {
  solar: {
    gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)',
    accent: '#9A7D3C',
    accentSoft: 'rgba(201,169,97,0.16)',
    label: 'Solar',
  },
  wind: {
    gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)',
    accent: '#3F6B82',
    accentSoft: 'rgba(63,107,130,0.14)',
    label: 'Wind',
  },
  hybrid: {
    gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)',
    accent: '#1F5A47',
    accentSoft: 'rgba(31,90,71,0.14)',
    label: 'Hybrid',
  },
  bess: {
    gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)',
    accent: '#6B5A8A',
    accentSoft: 'rgba(107,90,138,0.14)',
    label: 'BESS',
  },
  generic: {
    gradient: 'linear-gradient(180deg,#F8F2E2 0%,#F2EADB 100%)',
    accent: '#339274',
    accentSoft: 'rgba(51,146,116,0.12)',
    label: 'EPC',
  },
};
